import React, { useState } from 'react';
import { apiRoutes } from 'api_routes/api_routes';
import styled from 'styled-components';
import FiltrCompany from 'components/molecules/FiltrCompany/FiltrCompany';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DataGrid, plPL, ruRU, ukUA, enUS } from '@mui/x-data-grid';
import axiosInstance from 'api/axios';
import IconButton from '@mui/material/IconButton';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import MenuItem from '@mui/material/MenuItem';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import { Button } from '@mui/material';
import { theme } from 'theme/MainTheme';
import { useTranslation } from 'react-i18next';
import i18n from '../../../i18n';

const ButtonWrapper = styled.div`
  width: 220px;
  display: flex;
  justify-content: space-between;
`;

const DocTable = ({ listType, company, companyChange, type, typeChange, allItems }) => {
  const [selectionModel, setSelectionModel] = useState([]);
  const [error, setError] = useState("");
  const { t } = useTranslation();
  const rows = [];
  const activeLng = i18n.options.datagrid[i18n.language];

  const columns = [
    {
      field: 'documentId',
      headerName: t('doc_id'),
      flex: 1,
      minWidth: 150,
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: 'fileName',
      headerName: t('file'),
      flex: 1,
      minWidth: 430,
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: 'company',
      headerName: t('company'),
      flex: 1,
      minWidth: 200,
      sortable: false,
      disableColumnMenu: true,
      renderHeader: () => (
        <strong>
          {t('company')}
          <FiltrCompany
            onChange={companyChange}
            value={company}
            menuItems={[...new Set(allItems.map((row) => row.company))].map((item, index) => (
              <MenuItem key={index} value={item}>
                {item}
              </MenuItem>
            ))}
          />
        </strong>
      ),
    },
    {
      field: 'type',
      headerName: t('type'),
      flex: 1,
      minWidth: 200,
      sortable: false,
      disableColumnMenu: true,
      renderHeader: () => (
        <strong>
          {t('type')}
          <FiltrCompany
            onChange={typeChange}
            value={type}
            menuItems={[...new Set(allItems.map((row) => row.type))].map((item, index) => (
              <MenuItem key={index} value={item}>
                {item?.toUpperCase()}
              </MenuItem>
            ))}
          />
        </strong>
      ),
    },
    {
      field: 'downloadBtn',
      headerName: t('download'),
      flex: 1,
      minWidth: 80,
      sortable: false,
      disableColumnMenu: true,
      renderCell: () => (
        <IconButton
          edge="end"
          aria-label="download"
          onClick={(e) => {
            const rowId = Number(e.currentTarget.parentNode.parentNode.getAttribute('data-id'));
            const documentData = listType.find((obj) => obj.id === rowId);
            downloadFile(documentData.id);
          }}
        >
          <FileDownloadIcon fontSize="large" />
        </IconButton>
      ),
    },
  ];

  listType?.map((item) => {
    const obj = {
      id: item.id,
      documentId: item.payment_or_document_id,
      fileName: item.path.split('\\').pop(),
      company: item.company,
      type: item.type.toUpperCase(),
      date: item.date,
      downloadBtn: <FileDownloadIcon />,
      isNew: item.is_new,
    };
    rows.push(obj);
  });

  const downloadFile = (id) => {
    const documentObj = listType.find((obj) => obj.id === id);
    const row = document.querySelector(`[data-id="${id}"]`);
    row.classList.remove('super-app-theme-is-new--true');
    axiosInstance
      .get(apiRoutes.download_file, {
        params: {
          id,
        },
        headers: 'application/pdf',
        responseType: 'blob',
      })
      .then((res) => {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        const fileName = documentObj.path.split('\\').pop();

        link.href = url;
        if (typeof window.navigator.msSaveBlob === 'function') {
          window.navigator.msSaveBlob(res.data, fileName);
        } else {
          link.setAttribute('download', fileName);
          document.body.appendChild(link);
          link.click();
        }
        setError("");
      })
      .catch((err) => {
        if (err.response?.status === 404) {
          setError(t('no-file-info'));
        }

      });
  };

  const downloadCheckedFiles = () => {
    selectionModel.forEach((item) => {
      downloadFile(item);
    });
  };

  return (
    <>
      {error && (
        <Stack sx={{ width: '100%', paddingTop: '20px' }} spacing={0}>
          <Alert severity="error" sx={{ alignItems: 'center', fontSize: '1.2rem' }}>
            {error}
          </Alert>
        </Stack>
      )}
      <ButtonWrapper>
        <Button
          type="submit"
          variant="contained"
          onClick={downloadCheckedFiles}
          sx={{
            backgroundColor: theme.primary,
            mb: 1,
            mt: 1,
            width: '160px',
            height: '30px',
            fontSize: '0.75rem',
          }}
        >
          {t('download_checked')}
        </Button>
      </ButtonWrapper>
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={i18n?.language}>
        <Box
          sx={{
            height: 400,
            width: '100%',
            '& .super-app-theme-is-new--false': {
              fontWeight: 400,
            },
            '& .super-app-theme-is-new--true': {
              fontWeight: 800,
            },
          }}
        >
          <DataGrid
            localeText={
              activeLng === 'ukUA'
                ? ukUA.components.MuiDataGrid.defaultProps.localeText
                : activeLng === 'plPL'
                ? plPL.components.MuiDataGrid.defaultProps.localeText
                : activeLng === 'ruRU'
                ? ruRU.components.MuiDataGrid.defaultProps.localeText
                : enUS.components.MuiDataGrid.defaultProps.localeText
            }
            key={rows.id}
            rows={rows}
            columns={columns}
            pageSize={15}
            autoPageSize
            rowsPerPageOptions={[15, 25, 50]}
            checkboxSelection
            onSelectionModelChange={(itm) => setSelectionModel(itm)}
            selectionModel={selectionModel}
            disableSelectionOnClick
            disableColumnSelector
            getRowClassName={(params) => `super-app-theme-is-new--${params.row.isNew}`}
            sx={{
              height: '75vh',
              fontSize: 14,
              '& .MuiDataGrid-cell:focus, .MuiDataGrid-cell:focus-within, .MuiDataGrid-columnHeader:focus, .MuiDataGrid-columnHeader:focus-within':
                {
                  outline: 'none',
                },
              '@media (max-width: 700px)': {
                height: '100vh',
              },
              '@media (min-width: 600px)': {
                width: 'calc(100vw - 240px)',
              },
            }}
          />
        </Box>
      </LocalizationProvider>
    </>
  );
};

export default DocTable;
